<script setup lang="ts">
import { cn } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";
import { ChevronDown } from "lucide-vue-next";
import { useTemplating, buildTemplateContext } from "#bb/composables/useTemplating";

// 1) Expect 'id' from the parent.
const props = defineProps<{ id: string }>();
const emit = defineEmits(["content-update"]);

// 2) Access the definition data (read-only) via the content manager.
const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id));

// Inject report for templating item fields
const report = inject<any | null>("report", null);
const templateContext = computed(() => buildTemplateContext(report));
const logger = useLogger("AccordionItem");
const { applyTemplateObject } = useTemplating(templateContext, { logger });
const templated = computed(() => applyTemplateObject({
  title: definitionData.value?.title || "",
  number: definitionData.value?.number || "",
  icon: definitionData.value?.icon || ""
}));

const parentHighlightActive = inject<ref<boolean>>('accordion-highlight-active', ref(false))
const highlightActive = computed(() => {
  return  parentHighlightActive.value
})


// 4) Define commands for the context menu.
const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Accordion editors",
    commands: [
      {
        id: "edit",
        name: "Edit Item",
        description: "Edit accordion item settings",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: { ...definitionData.value },
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: "accordion-item",
    name: "Accordion Item",
    icon: "lucide:chevrons-up-down",
  },
});
</script>

<template>
  <AccordionItem
    :class="cn('border-b-transparent', $attrs.class ?? '')"
    :value="definitionData.id"
  >
    <AccordionTrigger
      class="group hover:no-underline [&[data-state=open]>.iconify]:rotate-180"
    >
      <div class="flex items-start space-x-4">
        <!-- The numbered square: fixed size and never shrinks -->
        <div
          v-if="templated.icon || templated.number"
          class="flex-none shrink-0 w-10 h-10 flex items-center justify-center rounded-sm"
          :class="
            highlightActive
              ? 'group-data-[state=open]:bg-primary group-data-[state=open]:text-primary-foreground group-data-[state=closed]:bg-background group-data-[state=closed]:border-2 group-data-[state=closed]:border-primary group-data-[state=closed]:text-primary'
              : 'bg-primary text-primary-foreground'
          "
        >
          <icon mode="svg" size="24"
            v-if="templated.icon"
            :name="templated.icon"
          />
            <span v-else>{{ templated.number }}</span>
        </div>
        <!-- Title container: expands as needed and is left aligned -->
        <div class="flex-1 text-left">
          {{ templated.title }}
        </div>
      </div>
      <template #icon>
        <ChevronDown
          class="pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200"
          :class="
            highlightActive
              ? 'group-data-[state=open]:text-primary text-muted-foreground'
              : 'text-muted-foreground'
          "
        />
      </template>
    </AccordionTrigger>

    <!-- Content with inner top shadow -->
    <AccordionContent>
      <div class="relative p-2 inset-shadow-sm rounded-sm text-foreground inset-shadow-sm dark:bg-muted/0">
        <slot />
      </div>
    </AccordionContent>
  </AccordionItem>

</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  componentId: ComponentDefinitionIds.ACCORDION_ITEM,
  editor: () => import("#bb/components/editor/container/accordion-item.vue"),
  icon: "lucide:chevrons-up-down",
  type: "Accordion Item",
  description:
    "An individual accordion item, containing a title and nested children blocks.",
  default: {
    id: crypto.randomUUID(),
    title: "Accordion Item",
    number: "1",
    expandAlignment: "right",
    children: [],
  },
  categories: ["Layout"],
  hiddenUntilFiltered: true,
};
</script>
